'use client';

import { useEffect, useRef } from 'react';

import { SubscribeEventsMap } from '../../lib/charting_library';
import { QuickStudyConfig, TradingViewDataManager, useTradingViewDataStorage } from '../misc/data-manager';
import TradingView from '../trading-view';
import { Button } from './button';
import IndicatorsIcon from './icons/indicators-icon.svg';
import { useTvHeaderContext } from './index';
import { applyTemplateForSymbol, resolveQuickStudyPlan } from './utils';

export const Indicators = () => {
  const restoring = useRef(false);
  const tv = useTvHeaderContext();

  const [template, setTemplate] = useTradingViewDataStorage({ key: 'template' });
  const [quickStudies] = useTradingViewDataStorage({ key: 'quickStudies' });

  useEffect(
    () =>
      tv.on('onSymbolChanged', () => {
        // Android Classic Mode에서 심볼 전환 시 onAutoSaveNeeded가 onFirstDataLoaded보다 먼저 발생하여
        // 이전 심볼의 template이 새 심볼에 저장되는 문제를 방어한다.
        // onSymbolChanged → onFirstDataLoaded 순서는 보장되므로, 여기서 잠그고 onFirstDataLoaded에서 해제한다.
        restoring.current = true;
      }),
    [tv],
  );

  // Restore: template → quick-studies (sequential, single flow)
  useEffect(() => {
    const off = tv.on('onFirstDataLoaded', async () => {
      if (tv.symbol === 'dummy') return;
      restoring.current = true;

      const raw = await template.asyncValue();
      const lastTemplate = raw[tv.symbol];

      if (lastTemplate != null) {
        tv.activeChart?.applyStudyTemplate(lastTemplate);
      }

      const storedQuickStudies = await quickStudies.asyncValue();
      // `getAllStudies` somtimes lie, need delay
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const currentAllStudies = new Set(tv.activeChart?.getAllStudies().map((s) => s.name));

      await restoreQuickStudies(tv, storedQuickStudies, currentAllStudies);

      restoring.current = false;
    });
    return () => {
      off();
    };
  }, [tv, template, quickStudies]);

  // Auto-save: track chart changes and persist template
  useEffect(() => {
    const chart = tv.widget;
    let dirty = false;

    const events: (keyof SubscribeEventsMap)[] = [
      'study',
      'study_event',
      'drawing',
      'drawing_event',
      'study_properties_changed',
      'series_properties_changed',
    ];

    const markDirty = () => {
      if (!restoring.current) dirty = true;
    };

    const save = () => {
      if (restoring.current || tv.symbol === 'dummy') dirty = false;
      if (!dirty || !tv.activeChart) return;
      dirty = false;

      const newTemplate = TradingViewDataManager.normalizeItem(
        tv.activeChart.createStudyTemplate({ saveSymbol: false, saveInterval: false }),
      );

      setTemplate((prev) => applyTemplateForSymbol(prev, tv.symbol, newTemplate ?? null));
    };

    for (const event of events) chart?.subscribe(event, markDirty);
    chart?.subscribe('onAutoSaveNeeded', save);

    return () => {
      if (!tv.enabled) return;
      try {
        for (const event of events) chart?.unsubscribe(event, markDirty);
        chart?.unsubscribe('onAutoSaveNeeded', save);
      } catch {
        // ignore internal error
      }
    };
  }, [setTemplate, tv]);

  return (
    <Button onClick={() => tv.activeChart?.executeActionById('insertIndicator')} tooltip='Indicators & Strategies'>
      <IndicatorsIcon width={20} height={20} />
    </Button>
  );
};

/**
 * quickStudies 설정 기반으로 chart의 quick-studies를 restore한다.
 * - enabled: true + template에 없음 → createStudy (즉시, 안전)
 * - enabled: false + template에 있음 → 한 프레임 대기 후 removeEntity
 * - 설정 없음 (key 자체가 없음) → template이 결정 (터치 안 함)
 */
async function restoreQuickStudies(
  tv: TradingView,
  storedQuickStudies: Record<string, QuickStudyConfig>,
  templateStudyNames: Set<string>,
) {
  if (!tv.activeChart) return;

  const { toAdd, toRemove } = resolveQuickStudyPlan(storedQuickStudies, templateStudyNames);

  toAdd.forEach((name) => {
    tv.activeChart?.createStudy(name, false, false, storedQuickStudies[name].inputs);
  });

  if (toRemove.length > 0) {
    // `getAllStudies` somtimes lie, need delay
    await new Promise((resolve) => requestAnimationFrame(resolve));
    tv.activeChart
      ?.getAllStudies()
      .filter((study) => toRemove.includes(study.name))
      .forEach((study) => tv.activeChart?.removeEntity(study.id));
  }
}
